import React, { useCallback, useContext, useMemo } from 'react';
import { Button, Chip, IconButton, Popover, Stack, Typography } from '@mui/material';
import { EditOutlined, DeleteOutlined, Description, Autorenew } from '@mui/icons-material';

import { useRouter } from '@cvt/routing';
import { BoxWell } from '@shared/components/BoxWell';
import { format } from 'date-fns';
import { useAnalysisCrud } from '@modules/Analysis/hooks/useAnalysisCrud';
import { DialogContext } from '@cvt/contexts/DialogContext';
import { FeedbackContext } from '@cvt/contexts';
import { Score } from '@modules/Analysis/components/Feedback/Score';

import { ChangeStatusAction } from './ChangeStatusAction';


type Props = Stories.Story & {
    onDelete: (id: number) => void;
};

export const StoryListItem: React.FC<Props> = (props) => {
  const { onDelete, ...story } = props;

  const router = useRouter();

  const { openDialog } = useContext(DialogContext);

  const { createStructuralAnalysis } = useAnalysisCrud();

  const { triggerFeedback } = React.useContext(FeedbackContext);
  const handleRunAnalysis = useCallback(
    async (id: number) => {
      try {
        await createStructuralAnalysis.mutateAsync({ id });
        openDialog('structuralAnalysis', { storyId: id });
      } catch {
        triggerFeedback({
          severity: 'error',
          message: 'Smth. went wrong' ,
        });
      }
    },
    [createStructuralAnalysis, openDialog, triggerFeedback],
  );

  const tags = useMemo(() => {
    const firstThreeTags = story.tags && story.tags.slice(0, 3);
    const remainingTags = story.tags && story.tags.slice(3);

    return {
      firstThreeTags,
      remainingTags,
    };
  },[story]);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  return (
    <BoxWell
      sx={{ p: 5, height: '100%' }}
    >
      <Stack
        justifyContent="space-between"
        height="100%"
        gap={2}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
          <Chip
            label={story.type}
            variant="outlined"
            sx={{
              height: 'auto',
              fontWeight: 500,
            }}
          />
          <Chip
            label={
              story.structuralFeedback
                ? (
                  <Score
                    type="structural"
                    score={story.structuralFeedback.scoreStructure}
                    render={({ progress }) => `Structural ${progress}`}
                  />
                )
                : 'Structural'
            }
            icon={<Description/>}
            variant="outlined"
            sx={{
              backgroundColor: story.structuralFeedback ? 'success.light' : 'transparent',
              color: story.structuralFeedback ? 'common.white' : 'text.primary',
              '& .MuiChip-icon': {
                color: story.structuralFeedback ? 'common.white' : 'text.primary',
                fontSize: '1.35em',
              },
            }}
            onClick={handleOpen}
          />

          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{
              paper: {
                sx: {
                  borderRadius: '20px',
                  overflow: 'hidden',
                },
                elevation: 4,
              },
            }}
          >
            <Stack direction="row" alignItems="center" gap={1} sx={{ p: 1.5 }}>
              <Button color="primary" onClick={()=>handleRunAnalysis(story.id)}>
                <Autorenew sx={{
                  animation: createStructuralAnalysis.isPending ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    from: {
                      transform: 'rotate(0deg)',
                    },
                    to: {
                      transform: 'rotate(360deg)',
                    },
                  },
                }}/>
                {story.structuralFeedback ? 'Re-run Structural Analysis' : 'Run Structural Analysis'}
              </Button>
            </Stack>
          </Popover>
        </Stack>

        <Typography
          variant="body1"
          fontSize={18}
          color={story.title ? 'text.primary' : 'text.secondary'}
          fontWeight={600}
          fontStyle={story.title ? 'normal' : 'italic'}
        >
          {story.title || 'Untitled Story'}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={400}>
          Last updated: {format(story.modified, 'MM/dd/yyyy')}
        </Typography>
        <Stack gap={2} direction="row" flexWrap="wrap" mt={1}>
          {tags.firstThreeTags && tags.firstThreeTags.map((tag, index) => (
            <Chip
              key={index}
              label={tag}
              color="secondary"
              sx={{
                height: 'auto',
                p: 0.1,
                fontWeight: 500,
                '& .MuiChip-label': {
                  whiteSpace: 'wrap',
                },
              }}
            />
          ))}

          {tags.remainingTags && tags.remainingTags.length > 0 && (
            <Chip
              label={`+${tags.remainingTags.length} more`}
              variant="outlined"
              sx={{
                height: 'auto',
                fontWeight: 500,
                '& .MuiChip-label': {
                  whiteSpace: 'wrap',
                },
              }}
            />
          )}
        </Stack>
        <Stack direction="row" gap={1} alignItems="center" justifyContent="space-between" width="100%">
          <Stack direction="row" gap={1}>
            <Chip
              label={story.status}
              sx={{
                height: 'auto',
                backgroundColor: theme => story.status === 'Draft' ? theme.palette.text.secondary : theme.palette.success.light,
                color: theme => story.status === 'Draft' ? 'white': theme.palette.success.contrastText,
                fontWeight: 500,
              }}
            />
            <ChangeStatusAction story={story}/>
          </Stack>
          <Stack direction="row" gap={1} alignItems="center">
            <IconButton
              onClick={(event) => [event.stopPropagation(), event.preventDefault(), router.stories.edit(story.id).go()]}
              color="primary"
              size="small"
            >
              <EditOutlined/>
            </IconButton>
            <IconButton
              onClick={(event) => [event.stopPropagation(), event.preventDefault(), onDelete(story.id)]}
              color="error"
              size="small"
            >
              <DeleteOutlined/>
            </IconButton>
          </Stack>
        </Stack>
      </Stack>
    </BoxWell>
  );
};
